// SEQ-169 claim timing: the one place a claim date and a payment due date are
// derived.
//
// WHY THIS IS DATE ONLY ARITHMETIC. The code this replaces built a local
// midnight Date and then formatted it as UTC:
//
//   const subDate = new Date(selectedYear, selectedMonth - 1, day);
//   setSubmissionDate(subDate.toISOString().split("T")[0]);
//
// In Brisbane (UTC+10) local 2026-09-25T00:00 is 2026-09-24T14:00Z, so a
// configured day of 25 populated the field with the 24th. Production carries
// eight claims dated the 24th with the exact 30 day signature between March and
// July 2026, so the defect has shipped repeatedly and staff retype the date.
//
// Every function here takes and returns calendar values (integers, or ISO
// yyyy-mm-dd strings) and never round trips a calendar date through a local
// time Date. The output is therefore identical in every timezone, which
// src/lib/claimTiming.test.ts asserts under TZ=Australia/Brisbane and TZ=UTC.
//
// The claim day clamps to the length of the month (31 in February gives the
// 28th, or the 29th in a leap year) and does NOT roll a weekend forward. The
// dashboard badge helper src/lib/claimDueDate.ts does roll weekends; adopting
// that here would move real claim dates, so this module deliberately does not
// call it.

/** Org fallback when no project config and no organisation setting resolve. */
export const DEFAULT_CLAIM_DAY = 25;

/** The period hardcoded as `+30` before this module existed. */
export const DEFAULT_PAYMENT_PERIOD_DAYS = 30;

const MIN_CLAIM_DAY = 1;
const MAX_CLAIM_DAY = 31;
const MIN_PAYMENT_PERIOD_DAYS = 0;
const MAX_PAYMENT_PERIOD_DAYS = 365;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The claim day and payment period in force for one project. */
export interface ProjectTimingDefaults {
  claimDay: number;
  paymentPeriodDays: number;
}

/**
 * Every source a project's timing can come from, in precedence order:
 * the project config row, then the organisation setting, then the constants
 * above. Null or absent means "not available", which is the state before the
 * config migration is applied.
 */
export interface ProjectTimingSources {
  configClaimDay?: number | null;
  configPaymentPeriodDays?: number | null;
  organizationClaimDay?: string | number | null;
}

/** Days in a calendar month. Month is 1 based. */
function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one, and Date.UTC keeps it
  // off the local clock.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function pad(value: number): string {
  return value.toString().padStart(2, "0");
}

function formatIsoDate(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, "0")}-${pad(month)}-${pad(day)}`;
}

/** Parsed parts of an ISO yyyy-mm-dd string, or null when it is not one. */
function parseIsoDate(
  value: string | null | undefined,
): { year: number; month: number; day: number } | null {
  if (!value) return null;
  const match = ISO_DATE.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;

  return { year, month, day };
}

function clampInteger(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

/**
 * A configured claim day coerced into 1..31.
 *
 * `claims_settings.default_claim_submission_day` is free text, so this accepts
 * a number or a string, takes the leading integer ("25th" gives 25) and falls
 * back to DEFAULT_CLAIM_DAY when the value is missing, empty, non numeric or
 * outside the range a CHECK constraint would accept.
 */
export function resolveClaimDay(
  raw: string | number | null | undefined,
  fallback: number = DEFAULT_CLAIM_DAY,
): number {
  const parsed = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
  if (!Number.isFinite(parsed)) return fallback;
  const truncated = Math.trunc(parsed);
  if (truncated < MIN_CLAIM_DAY || truncated > MAX_CLAIM_DAY) return fallback;
  return truncated;
}

/**
 * A configured payment period coerced into 0..365, falling back to
 * DEFAULT_PAYMENT_PERIOD_DAYS on anything unusable.
 */
export function resolvePaymentPeriodDays(
  raw: string | number | null | undefined,
  fallback: number = DEFAULT_PAYMENT_PERIOD_DAYS,
): number {
  const parsed = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
  if (!Number.isFinite(parsed)) return fallback;
  const truncated = Math.trunc(parsed);
  if (
    truncated < MIN_PAYMENT_PERIOD_DAYS ||
    truncated > MAX_PAYMENT_PERIOD_DAYS
  ) {
    return fallback;
  }
  return truncated;
}

/**
 * The timing a project actually claims on: its config row when it has one,
 * otherwise the organisation setting, otherwise the constants.
 */
export function resolveProjectTiming(
  sources: ProjectTimingSources,
): ProjectTimingDefaults {
  const organizationDay = resolveClaimDay(sources.organizationClaimDay);
  return {
    claimDay: resolveClaimDay(sources.configClaimDay, organizationDay),
    paymentPeriodDays: resolvePaymentPeriodDays(sources.configPaymentPeriodDays),
  };
}

/**
 * The claim date for a claim period: the configured day of the selected month,
 * clamped to the length of that month. Month is 1 based. Returns ISO yyyy-mm-dd.
 */
export function deriveClaimDate(
  year: number,
  month: number,
  claimDay: number,
): string {
  const safeMonth = clampInteger(month, 1, 12);
  const safeDay = clampInteger(claimDay, MIN_CLAIM_DAY, MAX_CLAIM_DAY);
  const day = Math.min(safeDay, daysInMonth(year, safeMonth));
  return formatIsoDate(year, safeMonth, day);
}

/**
 * Claim date plus a payment period in calendar days. Returns ISO yyyy-mm-dd, or
 * null when the claim date is not a valid ISO date (an empty or half typed date
 * input), which the caller treats as "leave the due date alone".
 */
export function deriveDueDate(
  claimDateIso: string | null | undefined,
  periodDays: number,
): string | null {
  const parts = parseIsoDate(claimDateIso);
  if (parts === null) return null;
  if (!Number.isFinite(periodDays)) return null;

  const shifted = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day + Math.trunc(periodDays)),
  );
  return formatIsoDate(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
  );
}

/**
 * Whole calendar days from one ISO date to another, or null when either is not
 * a valid ISO date. This is how the period actually used by a claim is
 * recovered once the operator has edited either date by hand.
 */
export function daysBetweenDates(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): number | null {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (start === null || end === null) return null;

  const startMs = Date.UTC(start.year, start.month - 1, start.day);
  const endMs = Date.UTC(end.year, end.month - 1, end.day);
  return Math.round((endMs - startMs) / 86_400_000);
}
